import React, { useEffect, useMemo, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  ButtonCheck,
  IconAccontSettings,
  IconChangePassword,
  IconLogout,
  IconRightMenuOpen,
  MenuFinoptsysLogo,
  MenuFinoptsysLogoHorizontal,
  NightMode,
} from '../../styles/svg/svgIcons';
import { getUserName } from './navigationBarHelper';
import { menuItems } from './navigationBarConstant';
import MenuOption from './MenuOption';
import { getAuthToken, getUserDetails } from '../../services/authService';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { paths } from '../../routes/routesConfig';
import {
  checkFeaturePermission,
  checkRoutePermission,
  getThemeFromLs,
} from '../../utils/featureUtils';
import { useDispatch } from 'react-redux';
import { setDarkTheme } from '../../app/appSlice';
import { genericLogout } from '../../app/appHelper';
import { authLogout } from '../../app/appActions';
import FsLoader from '../../common/fsLoader/fsLoaderComponent';
import { getClonedStringFromStorage } from '../../services/storageService';
import { permission } from '../../constants/permission';

const NavigationBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [showProfileOptions, setShowProfileOptions] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const currentLocation = useLocation();

  useEffect(() => {
    setShowProfileOptions(false);
  }, [isOpen, activeMenu]);

  useEffect(() => {
    if (!activeMenu) {
      const menuItem = menuItems().find((item) => item.path === currentLocation.pathname);
      if (menuItem) {
        setActiveMenu(menuItem.label);
      }
    } else {
      const menuItem = menuItems().find((item) => item.path === currentLocation.pathname);
      if (!menuItem) {
        setActiveMenu(null);
      }
    }
  }, [currentLocation.pathname]);

  const filteredMenuItems = menuItems().filter((item) => checkRoutePermission(item));
  const userDetails = getUserDetails(getAuthToken());
  const userName = getUserName(userDetails);
  const theme = getThemeFromLs();

  const toggleNavBar = () => {
    setIsOpen((prev) => !prev);
  };

  const toggleTheme = (theme) => {
    if (theme === 'dark') {
      localStorage.setItem('theme', 'light');
      dispatch(setDarkTheme({ isDark: false }));
    } else {
      localStorage.setItem('theme', 'dark');
      dispatch(setDarkTheme({ isDark: true }));
    }
    document
      .getElementsByTagName('html')[0]
      .setAttribute('data-theme', localStorage.getItem('theme'));
    setShowProfileOptions(false);
  };

  const handleLogout = () => {
    setLoading(true);
    const tenantCodeCopyToRedirect = getClonedStringFromStorage('tenant');
    genericLogout(() => dispatch(authLogout()), navigate, tenantCodeCopyToRedirect);
  };

  const hasAccountSettingsPermission = useMemo(() => {
    return (
      (checkFeaturePermission(permission.READ_ORG_SETUP) ||
        checkFeaturePermission(permission.WRITE_ORG_SETUP)) &&
      checkFeaturePermission(permission.WRITE_USER)
    );
  }, []);

  return (
    <>
      {loading && <FsLoader />}
      <div className={`right-nav loader-wrapper ${isOpen ? 'active' : ''}`}>
        <div className="nav-items-wrapper">
          <div
            className="right-menu-heading d-flex align-items-center cursor-pointer fix-h-67px"
            onClick={toggleNavBar}
          >
            <div className="d-flex arrow close-burgerpy-1">
              <IconRightMenuOpen />
            </div>
            {isOpen && <div className="ml-10 f-14 text-uppercase">Collapse Menu</div>}
          </div>
          <span
            className="nav-item arrow open-burger cursor-pointer border-none"
            onClick={toggleNavBar}
          >
            <div className="right-menu-wrapper text-grey-11">
              <IconRightMenuOpen />
            </div>
          </span>
          <div className="h-100 d-flex flex-column justify-space-between overflow-x-hidden">
            <PerfectScrollbar options={{ suppressScrollX: true, useBothWheelAxes: false }}>
              <div className="flex-fill overflow-auto menu-items-list">
                {filteredMenuItems.map((item) => (
                  <MenuOption
                    key={item.id}
                    isNavbarOpen={isOpen}
                    toggleNavBar={toggleNavBar}
                    label={item.label}
                    icon={item.navigationMenuIcon}
                    activeMenu={activeMenu}
                    setActiveMenu={setActiveMenu}
                    path={item?.path || ''}
                    subMenu={(item as any)?.subMenu || []}
                  />
                ))}
              </div>
            </PerfectScrollbar>
            {isOpen ? (
              <div className="d-flex finoptsys-menu-logo-horizontal">
                <div className="d-flex flex-fill">
                  <MenuFinoptsysLogoHorizontal />
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-center finoptsys-menu-logo px-2 py-3">
                <MenuFinoptsysLogo />
              </div>
            )}
          </div>
        </div>
        <>
          <div className="user-detail">
            <div className="media align-items-center">
              <div className="media-left" onClick={() => setShowProfileOptions((prev) => !prev)}>
                <div id="profile-image">{userName}</div>
              </div>
              <div
                className="media-right nav-profile-color w-150px"
                onClick={() => setShowProfileOptions((prev) => !prev)}
              >
                <div className="f-14 font-weight-600 d-flex align-items-center">
                  <span className="user-name" title={userDetails?.email || 'User'}>
                    {userDetails?.email || 'User'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`${isOpen ? 'wide-profile-options' : 'profile-options'} ${hasAccountSettingsPermission ? 'menu-option-4' : 'menu-option-3'}`}
          >
            <div className={`dropdown-menu mt-4 ${showProfileOptions ? 'show' : ''}`}>
              {hasAccountSettingsPermission && (
                <Link
                  to={paths.accountSettings}
                  className="dropdown-item"
                  role="button"
                  onClick={() => {
                    setShowProfileOptions(false);
                    setIsOpen(false);
                  }}
                >
                  <span>
                    <IconAccontSettings />
                  </span>
                  Account Settings
                </Link>
              )}

              <Link to={paths.changePassword} className="dropdown-item" role="button">
                <span>
                  <IconChangePassword />
                </span>
                Change Password
              </Link>
              <a
                href="#"
                onClick={() => toggleTheme(theme)}
                className="dropdown-item justify-content-between pr-0"
                role="button"
              >
                <span className="align-items-center">
                  <NightMode />
                  <span className="p-0 text-grey-10 pl-6">Dark Mode</span>
                </span>
                <span className={`ml-2 ${theme === 'light' ? 'd-none' : ''}`}>
                  <ButtonCheck />
                </span>
              </a>
              <a href="#" onClick={handleLogout} className="dropdown-item" role="button">
                <span>
                  <IconLogout />
                </span>
                Logout
              </a>
            </div>
          </div>
        </>
      </div>
    </>
  );
};

export default NavigationBar;
